import { UrlObject } from "url";
import { errorWithoutThrowInProduction } from "lib/logging/logHelper";
import Head from "next/head";
import React, { VFC, ReactElement } from "react";
import styled from "styled-components";
import ResetCss from "styles/ResetCss";
import SidebarLayout from "~/components/layout/SidebarLayout";
import { SITE_NAME } from "~/lib/constants";
import {
  getGoogleAnalyticsScriptAmpBody,
  getGoogleAnalyticsScriptAmpHeader,
} from "~/lib/utils/googleAnalytics";
import { Project } from "~/models/project";
import { Topic } from "~/models/topic";
import Footer from "faqSrc/components/modules/footer";
import HeaderSimpleWrap from "faqSrc/components/modules/header/simpleWrap";
import { pagesPath } from "faqSrc/lib/$path";
import { LayoutSize } from ".";

interface Props {
  asPath?: string;
  children: ReactElement;
  isAmp?: boolean;
  languageAlts?: string[];
  layoutSize?: LayoutSize;
  leftSidebar?: React.ReactNode;
  mainTitle: string | undefined;
  mainTitleUrl?: UrlObject;
  project: Project | undefined;
  rightSidebar?: React.ReactNode;
  showFooter?: boolean;
  showHeader?: boolean;
  showSearch?: boolean;
  subTitle?: string;
  subTitleUrl?: UrlObject;
  topic?: Topic;
}

function getMainTitle({
  mainTitle,
  project,
}: {
  mainTitle: string | undefined;
  project: Project | undefined;
}): string {
  const t = project?.siteName || mainTitle;

  // project か mainTitle は
  if (!t) {
    errorWithoutThrowInProduction({
      errorInfo: { mainTitle, project },
      errorMessage: "No Project & mainTitle",
    });
  }

  return t || SITE_NAME;
}

const LayoutPresenter: VFC<Props> = ({
  asPath,
  children,
  isAmp,
  languageAlts,
  layoutSize,
  leftSidebar,
  mainTitle,
  mainTitleUrl,
  project,
  rightSidebar,
  showFooter,
  showHeader,
  showSearch,
  subTitle,
  subTitleUrl,
  topic,
}) => (
  <>
    <Head>
      {isAmp &&
        getGoogleAnalyticsScriptAmpHeader(project?.googleAdsenseClientId)}
    </Head>
    {isAmp && getGoogleAnalyticsScriptAmpBody(project?.googleAnalyticsId)}
    <div id="page-top" />
    {showHeader && (
      <Container>
        <HeaderSimpleWrap
          layoutSize={layoutSize}
          mainTitle={getMainTitle({ mainTitle, project })}
          mainTitleUrl={mainTitleUrl || pagesPath.$url()}
          subTitle={subTitle}
          subTitleUrl={subTitleUrl}
          showSearch={showSearch}
          logoUrl={project?.logoPath || "/img/question-mark.svg"}
          project={project}
          topic={topic}
        />
      </Container>
    )}
    <SidebarLayout leftSidebar={leftSidebar} rightSidebar={rightSidebar}>
      {children}
    </SidebarLayout>
    {showFooter && (
      <Footer asPath={asPath} languageAlts={languageAlts} project={project} />
    )}
    <ResetCss />
  </>
);

const Container = styled.div`
  align-items: center;
  border-bottom: 1px solid var(--sep-outline-bdr-clr);
  display: flex;
  height: 48px;
  padding: 4px 0;
`;

export default LayoutPresenter;
